import {
  ErrorSubcode,
  type InspectSourceResult,
  type PrepareLogoResult,
  type RenderPdfRequest,
  type TServerErrorResponse,
} from "@/types";
import { isValidSubcode } from "@/lib/errorUtils";

/**
 * document-service client for the Deliverables feature: source inspection,
 * logo preparation and PDF rendering. Every call goes through the authenticated
 * `/api/document-proxy` route — never straight at document-service — and file
 * bytes only ever travel as an in-flight multipart body.
 */

/**
 * Every document-service deliverables path lives here, so a prefix change is a
 * one-line correction. The segment after the base is forwarded to
 * document-service verbatim, which is why `/document` appears here: that
 * service mounts every router under it so it can share a host with the
 * monolith, which owns `/api`.
 */
const DOCUMENT_PROXY_BASE = "/api/document-proxy";

export const documentServicePaths = {
  inspectSources: `${DOCUMENT_PROXY_BASE}/document/deliverable/inspect-sources`,
  prepareLogo: `${DOCUMENT_PROXY_BASE}/document/deliverable/prepare-logo`,
  renderPdf: `${DOCUMENT_PROXY_BASE}/document/deliverable/render-pdf`,
} as const;

/* -------------------------------------------------------------------------- */
/* Upload caps                                                                */
/* -------------------------------------------------------------------------- */

/** A single custodial statement or performance report. */
export const MAX_SOURCE_FILE_BYTES = 25 * 1024 * 1024; // 25 MB
/** All included source files in one inspection request. */
export const MAX_SOURCE_TOTAL_BYTES = 60 * 1024 * 1024; // 60 MB
/** The logo is embedded in the document as a data URI, so keep it small. */
export const MAX_LOGO_BYTES = 5 * 1024 * 1024; // 5 MB

const formatMegabytes = (bytes: number) =>
  `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;

/** Copy shown when one source file blows the per-file cap. */
export const sourceFileTooLargeMessage = (filename: string) =>
  `${filename} is ${formatMegabytes(MAX_SOURCE_FILE_BYTES)} or larger. Please upload a smaller file.`;

/** Copy shown when the selected files together blow the request cap. */
export const sourceTotalTooLargeMessage = () =>
  `Those files add up to more than ${formatMegabytes(MAX_SOURCE_TOTAL_BYTES)}. Please remove one or upload smaller files.`;

/** Copy shown when the logo blows its cap. */
export const logoTooLargeMessage = (filename: string) =>
  `${filename} is larger than ${formatMegabytes(MAX_LOGO_BYTES)}. Please upload a smaller logo.`;

/* -------------------------------------------------------------------------- */
/* Errors                                                                     */
/* -------------------------------------------------------------------------- */

interface DocumentServiceErrorBody {
  subcode?: unknown;
  statusCode?: unknown;
  message?: unknown;
  error?: unknown;
  detail?: unknown;
  [key: string]: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const parseJsonRecord = (
  value: string,
): DocumentServiceErrorBody | undefined => {
  try {
    const parsed = JSON.parse(value);
    return isRecord(parsed) ? (parsed as DocumentServiceErrorBody) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Shape a rejection like an axios error so `showErrorToast(error,
 * "deliverables")` and `parseApiError` work unchanged on these fetch-based
 * calls. Also used by the agent-service deliverables client.
 */
export const deliverableServiceError = (
  subcode: ErrorSubcode,
  statusCode: number,
  extra?: Record<string, unknown>,
): TServerErrorResponse =>
  ({
    response: {
      status: statusCode,
      data: { ...(extra ?? {}), subcode, statusCode },
    },
  }) as unknown as TServerErrorResponse;

/** Read a failed `Response` into the same error shape, preferring its subcode. */
export const readDeliverableServiceError = async (
  response: Response,
): Promise<TServerErrorResponse> => {
  const responseText = await response.text().catch(() => "");
  const body = responseText ? parseJsonRecord(responseText) : undefined;
  const nested = isRecord(body?.detail)
    ? (body.detail as DocumentServiceErrorBody)
    : undefined;
  const subcodeCandidate = nested?.subcode ?? body?.subcode;

  return deliverableServiceError(
    isValidSubcode(subcodeCandidate)
      ? subcodeCandidate
      : ErrorSubcode.UNKNOWN_ERROR,
    typeof body?.statusCode === "number" ? body.statusCode : response.status,
    { ...(body ?? {}) },
  );
};

const resourceLimitError = (message: string) =>
  deliverableServiceError(ErrorSubcode.RESOURCE_LIMIT, 413, { message });

/* -------------------------------------------------------------------------- */
/* Requests                                                                   */
/* -------------------------------------------------------------------------- */

interface DocumentServiceRequestOptions {
  signal?: AbortSignal;
}

/**
 * `POST /deliverable/inspect-sources` — one multipart request carrying every
 * selected source file, answered with a per-file verdict (page/sheet count,
 * token estimate, or a rejection reason). document-service persists nothing —
 * it reads the bytes, returns a verdict, and keeps neither. Storing the text it
 * extracted is the caller's decision, made against the saved deliverable.
 */
export const inspectSources = async (
  files: File[],
  options?: DocumentServiceRequestOptions,
): Promise<InspectSourceResult[]> => {
  const oversized = files.find((file) => file.size > MAX_SOURCE_FILE_BYTES);
  if (oversized) {
    throw resourceLimitError(sourceFileTooLargeMessage(oversized.name));
  }
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  if (totalBytes > MAX_SOURCE_TOTAL_BYTES) {
    throw resourceLimitError(sourceTotalTooLargeMessage());
  }

  const formData = new FormData();
  files.forEach((file) => formData.append("files", file, file.name));

  // No Content-Type header: the browser sets it with the multipart boundary,
  // and the proxy forwards that boundary untouched.
  const response = await fetch(documentServicePaths.inspectSources, {
    method: "POST",
    headers: { Accept: "application/json" },
    cache: "no-store",
    body: formData,
    signal: options?.signal,
  });

  if (!response.ok) {
    throw await readDeliverableServiceError(response);
  }

  const payload: unknown = await response.json().catch(() => null);
  if (Array.isArray(payload)) return payload as InspectSourceResult[];
  // document-service answers `{ files: [...] }` — one verdict per uploaded
  // file, in request order (`InspectSourcesResponse`).
  if (isRecord(payload) && Array.isArray(payload.files)) {
    return payload.files as InspectSourceResult[];
  }
  throw deliverableServiceError(
    ErrorSubcode.EXTERNAL_SERVICE_ERROR,
    response.status,
  );
};

/**
 * `POST /deliverable/prepare-logo` — normalises the uploaded image into the
 * data URI that gets embedded in the document permanently.
 */
export const prepareLogo = async (
  file: File,
  options?: DocumentServiceRequestOptions,
): Promise<PrepareLogoResult> => {
  if (file.size > MAX_LOGO_BYTES) {
    throw resourceLimitError(logoTooLargeMessage(file.name));
  }

  const formData = new FormData();
  formData.append("file", file, file.name);

  const response = await fetch(documentServicePaths.prepareLogo, {
    method: "POST",
    headers: { Accept: "application/json" },
    cache: "no-store",
    body: formData,
    signal: options?.signal,
  });

  if (!response.ok) {
    throw await readDeliverableServiceError(response);
  }

  const payload: unknown = await response.json().catch(() => null);
  if (!isRecord(payload) || typeof payload.dataUri !== "string") {
    throw deliverableServiceError(
      ErrorSubcode.EXTERNAL_SERVICE_ERROR,
      response.status,
    );
  }
  return payload as unknown as PrepareLogoResult;
};

/**
 * `POST /deliverable/render-pdf` — renders the saved, self-contained HTML and
 * streams back a PDF. `watermark` stamps `DRAFT — NOT REVIEWED`, so callers
 * pass `status === "DRAFT"`. Returns the blob; triggering the browser download
 * is the caller's job.
 */
export const renderDeliverablePdf = async (
  request: RenderPdfRequest,
  options?: DocumentServiceRequestOptions,
): Promise<Blob> => {
  const response = await fetch(documentServicePaths.renderPdf, {
    method: "POST",
    headers: {
      Accept: "application/pdf",
      "Content-Type": "application/json",
    },
    cache: "no-store",
    body: JSON.stringify(request),
    signal: options?.signal,
  });

  if (!response.ok) {
    throw await readDeliverableServiceError(response);
  }

  const blob = await response.blob();
  // A 200 carrying JSON or text means the renderer answered with something we
  // cannot download — treat it as an upstream failure rather than saving it.
  // An empty type is tolerated: some gateways drop the header on a stream.
  if (blob.type && !blob.type.includes("pdf")) {
    throw deliverableServiceError(
      ErrorSubcode.EXTERNAL_SERVICE_ERROR,
      response.status,
    );
  }
  return blob;
};
